import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, CircleCheck, CircleSlash, TrendingUp } from 'lucide-react'
import { api } from '../lib/api'
import { actionVerb, formatDateTime } from '../lib/format'
import { Card, CardBody, CardHeader, SectionHeading, StatTile } from '../components/ui/Card'
import { Chip } from '../components/ui/Badge'
import { EmptyState } from '../components/ui/DataTable'
import { SkeletonStats, SkeletonText } from '../components/ui/Skeleton'
import { useToast } from '../components/ui/Toast'

/**
 * The platform admin's landing page — how many client organisations exist,
 * which modules they lean on, and what has recently happened across the
 * platform. Everything here is landlord-level; nothing case-level from any
 * one tenant appears on this page.
 */
export default function Dashboard() {
  const toast = useToast()
  const [summary, setSummary] = useState(null)

  useEffect(() => {
    let cancelled = false

    api.get('/platform/dashboard')
      .then((data) => { if (!cancelled) setSummary(data) })
      .catch((err) => { if (!cancelled) toast.error(err.message) })

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-5">
      <SectionHeading
        level={1}
        title="Dashboard"
        description="Every client organisation this deployment serves, at a glance."
      />

      {!summary ? (
        <SkeletonStats count={4} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            icon={Building2}
            label="Tenants"
            value={summary.tenants.total}
            hint={`${summary.tenants.onboarded_this_month} onboarded this month`}
          />
          <StatTile
            icon={CircleCheck}
            label="Active"
            value={summary.tenants.active}
            tone="success"
          />
          <StatTile
            icon={CircleSlash}
            label="Suspended"
            value={summary.tenants.suspended}
            tone={summary.tenants.suspended > 0 ? 'warning' : 'neutral'}
          />
          <StatTile
            icon={TrendingUp}
            label="Platform admins"
            value={summary.platform_admins.total}
            hint={`${summary.platform_admins.active} active`}
          />
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Module adoption" description="How many tenants use each module." />
          <CardBody>
            {!summary ? (
              <SkeletonText lines={6} />
            ) : (
              <ul className="space-y-3">
                {Object.entries(summary.modules).map(([key, label]) => {
                  const count = summary.module_usage[key] ?? 0
                  const pct = summary.tenants.total > 0 ? Math.round((count / summary.tenants.total) * 100) : 0

                  return (
                    <li key={key}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[var(--color-ink-700)]">{label}</span>
                        <span className="tnum text-[var(--color-ink-500)]">{count} / {summary.tenants.total}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-[var(--color-surface-sunken)]">
                        <div
                          className="h-1.5 rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: 'var(--color-accent)' }}
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Recently onboarded" description="The five most recently provisioned tenants." />
          <CardBody padded={false}>
            {!summary ? (
              <div className="p-5"><SkeletonText lines={5} /></div>
            ) : summary.recent_tenants.length === 0 ? (
              <EmptyState icon={Building2} title="No tenants yet" />
            ) : (
              <ul className="divide-y divide-[var(--color-line)]">
                {summary.recent_tenants.map((tenant) => (
                  <li key={tenant.id}>
                    <Link
                      to={`/tenants/${tenant.id}`}
                      className="flex items-center justify-between gap-3 px-5 py-3 transition-colors duration-150 hover:bg-[var(--color-surface-sunken)]"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-[var(--color-ink-900)]">{tenant.name}</span>
                          {!tenant.is_active && <Chip tone="warning">Suspended</Chip>}
                        </div>
                        <p className="text-xs text-[var(--color-ink-500)]">{tenant.code}</p>
                      </div>
                      {tenant.onboarded_at && (
                        <span className="shrink-0 text-xs text-[var(--color-ink-500)]">{tenant.onboarded_at}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Recent activity"
          description="What platform administrators have done recently."
          actions={
            <Link to="/activity" className="text-sm font-medium text-[var(--color-accent-text)] hover:underline">
              View all
            </Link>
          }
        />
        <CardBody padded={false}>
          {!summary ? (
            <div className="p-5"><SkeletonText lines={5} /></div>
          ) : summary.recent_activity.length === 0 ? (
            <EmptyState title="Nothing yet" description="Provisioning, suspending or migrating a tenant will show up here." />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {summary.recent_activity.slice(0, 5).map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm text-[var(--color-ink-900)]">
                      <span className="font-medium">{entry.actor_name ?? 'Someone'}</span>{' '}
                      {actionVerb(entry.action)}{' '}
                      {entry.tenant_name && (
                        <Link to={`/tenants/${entry.tenant_id}`} className="font-medium text-[var(--color-accent-text)] hover:underline">
                          {entry.tenant_name}
                        </Link>
                      )}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-[var(--color-ink-500)]">{formatDateTime(entry.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
